import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import { Search, FileText, Loader2, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FaultCodes() {
  const [query, setQuery] = useState("");
  const [codes, setCodes] = useState([]);
  const [summary, setSummary] = useState({ distinct_codes: 0, total_entries: 0 });
  const [busy, setBusy] = useState(true);

  const load = useCallback(async (term) => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/support/fault-codes?query=${encodeURIComponent(term)}`);
      const data = await res.json();
      setCodes(data.codes || []);
      setSummary({ distinct_codes: data.distinct_codes || 0, total_entries: data.total_entries || 0 });
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => load(query), query ? 350 : 0);
    return () => clearTimeout(timer);
  }, [query, load]);

  return (
    <>
      <Seo
        title="Fault code lookup — Dyness and Solis alarm codes | EI Battery"
        description="Search the fault and alarm codes that appear in the Dyness and Solis manuals we supply, and open the exact manual page that lists them."
        canonical="/support/fault-codes"
      />
      <section className="bg-ei-navy text-ei-white py-12 sm:py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <p className="font-display text-xs tracking-[0.2em] text-ei-gold uppercase">Technical support</p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold mt-3">Fault code lookup</h1>
          <p className="text-ei-white/70 mt-4 max-w-2xl text-sm md:text-base leading-relaxed">
            Search the fault and alarm codes that appear in the manuals we supply. Every result links to the
            document and page so you can read the manufacturer's own table.
          </p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        <label className="relative block" htmlFor="fault-search">
          <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-ei-graphite/40" />
          <input
            id="fault-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search a code or symptom — E31, OV-BUS, 1021, overvoltage..."
            className="w-full border border-ei-mist rounded-md pl-11 pr-4 py-3 text-sm focus:outline-none focus:border-ei-navy min-h-[48px]"
            data-testid="fault-code-search"
          />
        </label>
        <p className="text-xs text-ei-graphite/55 mt-3" data-testid="fault-code-summary">
          {busy ? "Searching…" : `${summary.distinct_codes} codes shown · indexed from the approved manuals`}
        </p>

        <div className="mt-6 space-y-4" data-testid="fault-code-results">
          {busy && codes.length === 0 && (
            <div className="flex items-center gap-2 text-sm text-ei-graphite/60">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading the index…
            </div>
          )}
          {!busy && codes.length === 0 && (
            <div className="bg-white border border-ei-mist rounded-md p-6 text-sm text-ei-graphite/70">
              No code in our indexed manuals matches that. Ask the{" "}
              <Link to="/support" className="text-ei-navy underline underline-offset-2">AI technical assistant</Link>{" "}
              or request a specialist and we will confirm it from the manufacturer.
            </div>
          )}
          {codes.map((entry) => (
            <article key={entry.code} className="bg-white border border-ei-mist rounded-md p-5" data-testid={`fault-code-${entry.code.replace(/[^A-Za-z0-9]/g, "-")}`}>
              <div className="flex flex-wrap items-baseline gap-3">
                <h2 className="font-display text-lg font-bold text-ei-navy tnum">{entry.code}</h2>
                {entry.kind === "numeric" && <span className="text-[11px] uppercase tracking-wide text-ei-graphite/50">Solis event code</span>}
                {entry.kind === "alarm" && <span className="text-[11px] uppercase tracking-wide text-ei-graphite/50">Alarm name</span>}
              </div>
              {entry.description && <p className="text-sm text-ei-graphite/80 mt-2">{entry.description}</p>}
              <div className="mt-3 space-y-3">
                {entry.documents.slice(0, 3).map((doc, i) => (
                  <div key={`${entry.code}-${i}`} className="border-l-2 border-ei-gold/60 pl-3">
                    <a
                      href={`${doc.url}#page=${doc.page}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm font-display font-bold text-ei-navy hover:text-ei-gold inline-flex items-center gap-2"
                      data-testid={`fault-code-doc-${entry.code.replace(/[^A-Za-z0-9]/g, "-")}-${i}`}
                    >
                      <FileText className="w-3.5 h-3.5" /> {doc.title} — page {doc.page}
                    </a>
                    {doc.revision_date && <span className="text-xs text-ei-graphite/50 ml-2">rev {doc.revision_date}</span>}
                    {doc.text_recognition && (
                      <span className="text-xs text-amber-700 ml-2">text read by recognition — check the page</span>
                    )}
                    {doc.extract && (
                      <p className="text-xs text-ei-graphite/60 mt-1 leading-relaxed line-clamp-3">{doc.extract}</p>
                    )}
                  </div>
                ))}
              </div>
            </article>
          ))}
        </div>

        <div className="mt-10 bg-ei-navy/5 border border-ei-navy/10 rounded-md p-5 flex gap-3">
          <AlertTriangle className="w-5 h-5 text-ei-navy shrink-0 mt-0.5" />
          <p className="text-sm text-ei-graphite/80 leading-relaxed">
            Manual fault tables lose their column alignment when text is extracted from a PDF, so we show you the
            code and the page rather than guessing what each one means. Always read the manufacturer's table, and
            leave wiring, commissioning and electrical testing to your licensed electrical contractor.
          </p>
        </div>
      </section>
    </>
  );
}
